import * as React from 'react';
import { clamp } from 'es-toolkit';
import { cn } from '../utils';
import { getTestId, type TestIdProps } from '../utils/testId';
import {
  progressFillVariants,
  progressTrackVariants,
  type ProgressBarVariant,
  type ProgressTrackVariantProps
} from './progress-bar-variants';

export type { ProgressBarVariant } from './progress-bar-variants';
export { PROGRESS_FILL_CLASS, PROGRESS_TRACK_CLASS } from './progress-bar-variants';

export interface ProgressBarProps extends ProgressTrackVariantProps, TestIdProps {
  /** Current progress as a 0–100 percentage. Values outside the range are clamped. */
  value: number;
  /** Colour of the fill. @default 'primary' */
  variant?: ProgressBarVariant | null;
  /** Accessible label describing what the bar represents. */
  ariaLabel?: string;
  className?: string;
}

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, size, muted, variant, ariaLabel, className, testId, id, name }, ref) => {
    const clamped = clamp(value, 0, 100);

    return (
      <div
        ref={ref}
        role='progressbar'
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
        data-testid={getTestId({ testId, id, name })}
        className={cn(progressTrackVariants({ size, muted }), className)}
      >
        <div className={progressFillVariants({ variant })} style={{ width: `${clamped}%` }} />
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';
